/**
 * Auto-detect card references and convert them into work-item chips.
 *
 * Two things become the same inline chip the wiki slash-menu picker
 * inserts — kind stripe, mono code, title, status pill, link to the card:
 *
 *   - a typed shorthand, `MOTE-30`, followed by a space or sentence
 *     punctuation;
 *   - a link to a card in THIS workspace, pasted or typed — the card's own
 *     page (`/MOTE/tickets/MOTE-30`), an event page, or a board URL with
 *     `?selectedDeliverable=`. `parseCardDeepLink` owns which shapes count.
 *
 * ## Verify BEFORE converting — the whole design follows from this
 *
 * The typed or pasted text is left ALONE while two checks run, and only a
 * confirmed hit ever mutates the document:
 *
 *   1. A free, synchronous pre-filter. For a shorthand that is the
 *      tenant's live project list (`getProjectCodes`), which keeps
 *      `COVID-19`, `ISO-8601`, `UTF-8` and `H-1B` from firing a request,
 *      let alone becoming chips. For a URL it is `parseCardUrl`, which
 *      rejects anything that isn't a same-origin card link — another
 *      tenant, another stage, or any other page of our own app.
 *   2. The reference is resolved against the BE (`resolveReference`),
 *      which confirms the work item actually EXISTS and returns
 *      everything the chip renders.
 *
 * A 404, a network error, or a mid-flight edit to the candidate text means
 * NOTHING HAPPENS — the text was never touched, so there is no spinner, no
 * placeholder chip, and no revert flash. The alternative shape
 * (insert-a-provisional-chip-then-resolve) was rejected for exactly that:
 * a chip that appears, spins, and vanishes is worse than plain text that
 * quietly stays plain. For a pasted link the fallback is better still:
 * what stays behind is the working link the user pasted.
 *
 * The consequence worth knowing downstream: an auto-detected chip is ALWAYS
 * born fully resolved, with `cardId` / `projectId` / `kind` / `shorthand`
 * all set. `WorkItemEmbedView` therefore never renders a resolving state
 * for this path.
 *
 * ## Why input rules PLUS a plugin
 *
 * Input rules are synchronous; the second check is a network call. So a
 * rule only DETECTS (it deliberately dispatches no steps, which is how
 * ProseMirror knows to let the typed delimiter through), and a plugin holds
 * the in-flight candidates and maps their positions through every
 * subsequent transaction. That mapping is what lets the user keep typing —
 * anywhere in the document — while a reference resolves behind them; the
 * chip lands at the right place regardless of how much text moved.
 *
 * A PASTE is not text input, so it reaches no input rule. The same plugin
 * therefore scans the changed ranges of any transaction batch carrying
 * ProseMirror's `uiEvent: 'paste'` meta and registers candidates the same
 * way. It must be a scan rather than a ProseMirror PASTE RULE, because a
 * paste rule rewrites the slice synchronously — which is precisely the
 * un-verified conversion this whole module exists to avoid.
 *
 * Position math mirrors `mark-input-rules.ts`: the runner's text extraction
 * substitutes an atom's `renderText()` (a mention chip's `@Label` is N
 * characters for 1 document position), so the handler re-extracts a
 * position-faithful string before computing any range. Read that file's
 * header before touching `locateCandidate`.
 */

import { closeHistory } from '@tiptap/pm/history'
import type { MarkType, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import { Extension, InputRule } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import { combineTransactionSteps, findChildrenInRange, getChangedRanges } from '@tiptap/react'
import { find } from 'linkifyjs'

import type { DeliverableKind } from '@/api/types-bridge'
import type { CardReference } from '@/features/board/card-deep-link'

/** One character per document position for anything the rule must not span. */
const OPAQUE = '￼'

/**
 * Fires on the delimiter that ENDS a reference. Group 1 is the reference
 * itself; the trailing class is the just-typed character and is never
 * consumed as part of it.
 *
 * The lookbehind rejects a reference glued to preceding word characters,
 * a hyphen, a slash, or an equals sign — `v2-MOTE-30` and
 * `MOTE-30/MOTE-31` are not two clean references, `2026-30` is not a
 * reference at all, and `?selectedDeliverable=MOTE-30` is a query-string
 * value inside a URL that the URL rule is already handling. Sentence
 * punctuation and brackets DO delimit, so `(MOTE-30)` works.
 *
 * That last exclusion is defence in depth rather than decoration: without
 * it a typed board URL registers BOTH a URL candidate and a shorthand
 * candidate over the same characters, and the shorthand one is refused
 * only because autolink happens to have marked the range as a link. Were
 * autolink ever off on a surface, the shorthand chip would commit INSIDE
 * the URL and mangle it.
 *
 * The code shape (letter-initial, 2–5 chars) mirrors the BE's
 * `projects.code` CHECK. It is only a cheap pre-filter — `getProjectCodes`
 * is what decides whether a matching string is a real project.
 */
const REFERENCE_TRIGGER = /(?<![\w\-/=])([A-Za-z][A-Za-z0-9]{1,4}-\d{1,9})[\s.,;:!?)\]}]$/

/**
 * Fires on the whitespace that ENDS a typed URL. Group 1 is the
 * whitespace-delimited token; linkify then decides how much of it is
 * actually the link.
 */
const TYPED_URL_TRIGGER = /(?<!\S)(https?:\/\/\S+)\s$/i

/** What the BE resolver tells us about a confirmed reference. */
export interface ResolvedWorkItem {
  id: string
  projectId: string
  kind: DeliverableKind
  /** Canonical shorthand as stored (`MOTE-30`), whatever case was typed. */
  code: string
}

export interface WorkItemAutodetectOptions {
  /**
   * Live project codes for the current tenant, uppercase. An empty list
   * disables SHORTHAND detection entirely — which is the correct behavior
   * while the projects query is still in flight: better to convert nothing
   * than to fire a request per typed word. URL detection is unaffected,
   * because a card URL names its card without consulting the catalog.
   */
  getProjectCodes: () => readonly string[]
  /**
   * Same-origin card URL → the reference it names, else null. Synchronous
   * and pure; the caller binds it to the current origin.
   */
  parseCardUrl: (href: string) => CardReference | null
  /**
   * Resolves a reference, or `null` when no such work item exists.
   * Expected to be cache-backed (including negative results) so retyping
   * the same bogus number doesn't re-hit the network.
   */
  resolveReference: (reference: CardReference) => Promise<ResolvedWorkItem | null>
}

/**
 * Where a candidate came from, which is the ONLY thing the commit guard
 * treats differently — see `candidateMarksAllow`.
 */
type CandidateSource =
  | { kind: 'shorthand' }
  /** `href` is what linkify derives from the matched text, i.e. what the link mark will carry. */
  | { kind: 'url'; href: string }

interface PendingCandidate {
  id: number
  /** Start of the reference text, mapped forward on every transaction. */
  from: number
  to: number
  /** Exactly the characters currently expected between `from` and `to`. */
  text: string
  reference: CardReference
  source: CandidateSource
}

interface AutodetectPluginState {
  pending: readonly PendingCandidate[]
}

type AutodetectMeta =
  | { type: 'add'; candidate: PendingCandidate }
  | { type: 'addMany'; candidates: readonly PendingCandidate[] }
  | { type: 'drop'; id: number }

const autodetectKey = new PluginKey<AutodetectPluginState>('workItemAutodetect')

let nextCandidateId = 1

/**
 * May the text in this range become a chip, given the marks on it?
 *
 * Inline `code` always refuses: there the reference is a literal, quoted
 * precisely so it stays text.
 *
 * A `link` mark is where the two sources part company. A typed SHORTHAND
 * inside a link means the author already chose a destination, so it
 * refuses. A URL candidate, on the other hand, is EXPECTED to carry one —
 * TipTap's link paste rule adds it in the same dispatch as the paste, long
 * before our resolve returns. What we require instead is that every link
 * mark on the range points where the text itself points: that is the
 * difference between `https://…/MOTE-30` (the text IS the URL, ours to
 * convert) and `[see the ticket](https://…/MOTE-30)` (the author wrote
 * display text over it — theirs, leave it). A range with NO link mark is
 * fine too: copying from another ProseMirror editor puts `data-pm-slice`
 * on the clipboard, which makes TipTap skip its paste rules entirely.
 */
function candidateMarksAllow(
  state: EditorState,
  from: number,
  to: number,
  source: CandidateSource,
): boolean {
  const codeMark: MarkType | undefined = state.schema.marks.code
  if (codeMark != null && state.doc.rangeHasMark(from, to, codeMark)) return false

  const linkMark: MarkType | undefined = state.schema.marks.link
  if (linkMark == null) return true
  if (!state.doc.rangeHasMark(from, to, linkMark)) return true
  if (source.kind === 'shorthand') return false

  let everyLinkAgrees = true
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return
    for (const mark of node.marks) {
      if (mark.type !== linkMark) continue
      if (mark.attrs.href !== source.href) everyLinkAgrees = false
    }
  })
  return everyLinkAgrees
}

/**
 * A position-faithful rendering of one textblock's inline content: exactly
 * one character per document position, with code-marked text and inline
 * atoms opaque so nothing can match across them. Offsets into the returned
 * string are therefore offsets into the block, and `blockStart + offset` is
 * a real document position.
 *
 * Every inline non-text node in this schema is a nodeSize-1 leaf — see the
 * equivalent walk in `mark-input-rules.ts` before adding an inline node
 * with children.
 */
function faithfulTextOf(block: ProseMirrorNode, limitOffset?: number): string {
  const limit = limitOffset ?? block.content.size
  let out = ''
  block.nodesBetween(0, limit, (node, pos) => {
    if (node.isText) {
      const chunk = (node.text ?? '').slice(0, Math.max(0, limit - pos))
      out += node.marks.some((m) => m.type.spec.code) ? OPAQUE.repeat(chunk.length) : chunk
    } else {
      out += OPAQUE
    }
    return false
  })
  return out
}

/**
 * The single linkify match covering `token`, or null when the token holds
 * no URL or more than one. Called with NO options, mirroring the call
 * TipTap's own link paste rule makes, so the `href` we record is the href
 * the mark will carry — the comparison `candidateMarksAllow` depends on.
 */
function soleUrlIn(token: string): { href: string; value: string; start: number } | null {
  const matches = find(token).filter((m) => m.isLink && m.type === 'url')
  const only = matches.length === 1 ? matches[0] : undefined
  if (!only) return null
  return { href: only.href, value: only.value, start: only.start }
}

/**
 * Phase 2 of the match: rebuild a position-faithful string for the current
 * textblock and re-run the trigger against it, so the returned range is
 * real document positions rather than the runner's arithmetic.
 */
function locateCandidate(
  state: EditorState,
  caret: number,
  typedLength: number,
  getProjectCodes: () => readonly string[],
  typedDelimiter: string,
): { from: number; to: number; text: string } | null {
  if (typedLength !== 1) return null

  const $caret = state.doc.resolve(caret)
  if (!$caret.parent.isTextblock || $caret.parent.type.spec.code) return null

  const blockStart = $caret.start()
  const faithful = faithfulTextOf($caret.parent, $caret.parentOffset)

  const match = REFERENCE_TRIGGER.exec(faithful + typedDelimiter)
  const reference = match?.[1]
  if (match == null || reference == null) return null

  const from = blockStart + match.index
  const to = from + reference.length
  if (from < blockStart || to > caret) return null

  const code = reference.slice(0, reference.lastIndexOf('-')).toUpperCase()
  if (!getProjectCodes().includes(code)) return null

  if (!candidateMarksAllow(state, from, to, { kind: 'shorthand' })) return null

  return { from, to, text: reference }
}

/**
 * The typed-URL counterpart of `locateCandidate`. Fires on whitespace
 * rather than sentence punctuation, matching the autolink plugin — which
 * runs on the same keystroke and adds the mark the commit guard then
 * permits.
 *
 * An explicit `http(s)://` scheme is REQUIRED, and that is not fussiness:
 * linkify resolves a scheme-less domain to an `http://` href, whose origin
 * can never equal an `https://` workspace, so such a candidate could only
 * ever be rejected later. Requiring the scheme up front keeps the href we
 * record, the href the mark carries, and the origin check all consistent.
 */
function locateUrlCandidate(
  state: EditorState,
  caret: number,
  typedLength: number,
  parseCardUrl: (href: string) => CardReference | null,
  typedDelimiter: string,
): { from: number; to: number; text: string; reference: CardReference; href: string } | null {
  if (typedLength !== 1) return null
  if (!/\s/.test(typedDelimiter)) return null

  const $caret = state.doc.resolve(caret)
  if (!$caret.parent.isTextblock || $caret.parent.type.spec.code) return null

  const blockStart = $caret.start()
  const faithful = faithfulTextOf($caret.parent, $caret.parentOffset)

  const match = TYPED_URL_TRIGGER.exec(faithful + typedDelimiter)
  const token = match?.[1]
  if (match == null || token == null) return null

  // Take the range from linkify rather than from the whitespace-delimited
  // token, so trailing punctuation (`…/MOTE-30.`) is excluded the same way
  // the link mark will exclude it.
  const link = soleUrlIn(token)
  if (!link) return null

  const reference = parseCardUrl(link.href)
  if (!reference) return null

  const from = blockStart + match.index + link.start
  const to = from + link.value.length
  if (from < blockStart || to > caret) return null

  if (!candidateMarksAllow(state, from, to, { kind: 'url', href: link.href })) return null

  return { from, to, text: link.value, reference, href: link.href }
}

/**
 * Mirrors the trigger regex's `(?<![\w\-/])` lookbehind against the live
 * document. An empty result means the reference starts at a block
 * boundary, which is delimited by definition.
 */
function hasClearLeftBoundary(state: EditorState, from: number): boolean {
  if (from <= 0) return true
  const preceding = state.doc.textBetween(Math.max(0, from - 1), from)
  return preceding === '' || !/[\w\-/=]/.test(preceding)
}

function dispatchMeta(editor: Editor, meta: AutodetectMeta): void {
  if (editor.isDestroyed) return
  editor.view.dispatch(editor.state.tr.setMeta(autodetectKey, meta))
}

/**
 * Replaces the (mapped) candidate range with a fully-resolved chip — but
 * only if the range still holds exactly the text we verified. The user may
 * have edited or deleted it while the request was in flight, in which case
 * the confirmation is stale and converting would corrupt what they typed.
 */
function commitCandidate(editor: Editor, id: number, card: ResolvedWorkItem): void {
  if (editor.isDestroyed) return

  const pending = autodetectKey.getState(editor.state)?.pending ?? []
  const entry = pending.find((candidate) => candidate.id === id)
  const tr = editor.state.tr.setMeta(autodetectKey, { type: 'drop', id } satisfies AutodetectMeta)

  const nodeType = editor.state.schema.nodes.workItemEmbed
  const stillEligible =
    entry != null &&
    editor.isEditable &&
    nodeType != null &&
    // Re-assert the input rule's own lookbehind. `from` tracks insertions
    // that land before the reference (see the mapping comment), which is
    // what lets an ordinary shift keep resolving — but it means an insert
    // DIRECTLY against the reference's start slips through the text
    // compare too. Without this, `MOTE-30 ` → caret home → `x` would mint
    // a chip for `xMOTE-30`, which typing the same characters in order
    // could never produce.
    hasClearLeftBoundary(editor.state, entry.from) &&
    // Width check BEFORE the text compare, and it is not redundant with
    // it: `textBetween` contributes nothing for a block boundary or an
    // inline atom, so a range that grew to span a paragraph split — or to
    // swallow a mention chip the user just inserted — still reads exactly
    // "MOTE-30". Replacing it would then silently re-join the paragraph
    // or delete the chip. Positions are only comparable to a character
    // count because the verified text is plain characters by construction.
    entry.to - entry.from === entry.text.length &&
    editor.state.doc.textBetween(entry.from, entry.to) === entry.text &&
    candidateMarksAllow(editor.state, entry.from, entry.to, entry.source)

  if (entry != null && stillEligible) {
    // Force a new history group so ONE undo reverts the chip back to the
    // typed reference and stops there. Without this, prosemirror-history's
    // 500ms `newGroupDelay` folds the conversion into the same undo event
    // as the typing that preceded it — so a cache-warm resolution (well
    // under 500ms) made Cmd+Z wipe the whole phrase instead of just
    // undoing the chip. Caught by the undo case in the spec.
    closeHistory(tr)
    tr.replaceWith(
      entry.from,
      entry.to,
      nodeType.create({
        cardId: card.id,
        projectId: card.projectId,
        kind: card.kind,
        // The stored casing, not the typed casing, so the chip and the
        // card agree and the BE's shorthand attr check passes.
        shorthand: card.code,
      }),
    )

    // The link mark is INCLUSIVE while autolink is on, so replacing a
    // linked URL leaves it in `storedMarks` and the very next character
    // typed would be linked to a URL that is no longer in the document.
    const linkMark: MarkType | undefined = editor.state.schema.marks.link
    if (linkMark != null) {
      const stored = tr.storedMarks ?? editor.state.storedMarks
      if (stored?.some((mark) => mark.type === linkMark)) tr.removeStoredMark(linkMark)
    }
  }

  editor.view.dispatch(tr)
}

/**
 * Register a candidate and start verifying it. Shared by all three
 * detection paths so the ordering guarantee holds in each: the plugin
 * learns about the candidate BEFORE any response can arrive, which
 * matters because a cache hit resolves in the same microtask queue.
 *
 * `alreadyRegistered` is for the paste scan, which runs inside
 * `appendTransaction` and therefore registers its candidates through the
 * transaction it returns. Dispatching `add` for those as well would put a
 * SECOND copy in `pending` carrying the positions as they were at scan
 * time — unmapped from then on, because only the copy already in the list
 * when a transaction applies gets mapped. The duplicate is harmless today
 * (`drop` removes both, and `find` reaches the mapped copy first) and
 * would become a mis-commit the moment any plugin ordered after this one
 * shifted positions during a paste.
 */
function trackCandidate(
  editor: Editor,
  candidate: PendingCandidate,
  resolveReference: (reference: CardReference) => Promise<ResolvedWorkItem | null>,
  options: { alreadyRegistered?: boolean } = {},
): void {
  queueMicrotask(() => {
    if (editor.isDestroyed) return
    if (!options.alreadyRegistered) dispatchMeta(editor, { type: 'add', candidate })
    void resolveReference(candidate.reference)
      .then((card) => {
        if (card) commitCandidate(editor, candidate.id, card)
        else dispatchMeta(editor, { type: 'drop', id: candidate.id })
      })
      .catch(() => {
        // No such card, or the lookup failed. Either way the text was
        // never touched, so there is nothing to undo.
        dispatchMeta(editor, { type: 'drop', id: candidate.id })
      })
  })
}

/**
 * Scan the ranges a PASTE just changed for links to cards in this
 * workspace, and register one candidate per hit.
 *
 * Returns candidates rather than dispatching, because this runs inside
 * `appendTransaction` where the only legal output is a transaction.
 */
function scanPastedRanges(
  transactions: readonly Transaction[],
  oldState: EditorState,
  newState: EditorState,
  parseCardUrl: (href: string) => CardReference | null,
): PendingCandidate[] {
  const transform = combineTransactionSteps(oldState.doc, [...transactions])
  const found: PendingCandidate[] = []
  const existing = autodetectKey.getState(newState)?.pending ?? []
  // One textblock can sit under several changed ranges (one per step);
  // visit each once so the same URL is not registered twice.
  const seenBlocks = new Set<number>()

  for (const { newRange } of getChangedRanges(transform)) {
    const blocks = findChildrenInRange(newState.doc, newRange, (node) => node.isTextblock)
    for (const { node: block, pos } of blocks) {
      if (seenBlocks.has(pos)) continue
      seenBlocks.add(pos)
      if (block.type.spec.code) continue

      const blockStart = pos + 1
      const faithful = faithfulTextOf(block)

      for (const match of find(faithful)) {
        if (!match.isLink || match.type !== 'url') continue
        const reference = parseCardUrl(match.href)
        if (!reference) continue

        const from = blockStart + match.start
        const to = blockStart + match.end
        // Only what this paste actually brought in. A URL already sitting
        // in the same paragraph is not a candidate — the user pasted
        // beside it, not it.
        if (from < newRange.from || to > newRange.to) continue

        const source: CandidateSource = { kind: 'url', href: match.href }
        if (!candidateMarksAllow(newState, from, to, source)) continue

        const text = match.value
        const duplicate =
          existing.some((c) => c.from === from && c.to === to && c.text === text) ||
          found.some((c) => c.from === from && c.to === to && c.text === text)
        if (duplicate) continue

        found.push({ id: nextCandidateId++, from, to, text, reference, source })
      }
    }
  }

  return found
}

export function buildWorkItemAutodetectExtension(options: WorkItemAutodetectOptions) {
  return Extension.create<WorkItemAutodetectOptions>({
    name: 'workItemAutodetect',

    addOptions() {
      return options
    },

    addProseMirrorPlugins() {
      const { parseCardUrl, resolveReference } = this.options
      const editor = this.editor

      return [
        new Plugin<AutodetectPluginState>({
          key: autodetectKey,
          // A paste reaches no input rule, so this is where a pasted card
          // link is detected. It adds NO steps — only meta — so the caret,
          // the selection and the undo history are untouched, exactly as
          // with the typing path.
          appendTransaction(transactions, oldState, newState) {
            const pasted = transactions.some(
              (tr) => tr.docChanged && tr.getMeta('uiEvent') === 'paste',
            )
            if (!pasted) return null

            const candidates = scanPastedRanges(transactions, oldState, newState, parseCardUrl)
            if (candidates.length === 0) return null

            for (const candidate of candidates) {
              trackCandidate(editor, candidate, resolveReference, { alreadyRegistered: true })
            }
            return newState.tr.setMeta(autodetectKey, {
              type: 'addMany',
              candidates,
            } satisfies AutodetectMeta)
          },
          state: {
            init: () => ({ pending: [] }),
            apply(tr, value) {
              let pending = value.pending
              if (tr.docChanged && pending.length > 0) {
                pending = pending
                  .map((candidate) => ({
                    ...candidate,
                    // Both edges take assoc `1`, and the two edges want it
                    // for OPPOSITE reasons — which is why neither a
                    // uniformly "inward" nor a uniformly "outward" bias is
                    // right.
                    //
                    // `to` is the position AFTER the last character, so an
                    // insert there EXTENDS the reference (`MOTE-30` +
                    // `1`). The range must grow to include it so the
                    // commit guard's text compare trips. This was the bug:
                    // with assoc -1 the range stayed pinned to the
                    // original seven characters, `textBetween` still read
                    // "MOTE-30", and `MOTE-30 ` → backspace → `1` accepted
                    // the stale confirmation and minted a chip for the
                    // WRONG CARD followed by an orphan `1`.
                    //
                    // `from` is the position OF the first character, so an
                    // insert there lands BEFORE the reference and must
                    // push `from` forward — otherwise typing at the start
                    // of the paragraph stops the range tracking the
                    // reference at all, and an ordinary shift is refused.
                    // The adjacent-prepend case that assoc -1 would have
                    // caught (`x` + `MOTE-30`, which the input rule's own
                    // lookbehind would refuse) is handled at commit time
                    // by re-asserting that boundary instead.
                    from: tr.mapping.map(candidate.from, 1),
                    to: tr.mapping.map(candidate.to, 1),
                  }))
                  // A collapsed range means the text was deleted out from
                  // under us; there is nothing left to convert.
                  .filter((candidate) => candidate.to > candidate.from)
              }
              const meta = tr.getMeta(autodetectKey) as AutodetectMeta | undefined
              if (meta?.type === 'add') pending = [...pending, meta.candidate]
              if (meta?.type === 'addMany') pending = [...pending, ...meta.candidates]
              if (meta?.type === 'drop') {
                pending = pending.filter((candidate) => candidate.id !== meta.id)
              }
              return pending === value.pending ? value : { pending }
            },
          },
        }),
      ]
    },

    addInputRules() {
      const { getProjectCodes, parseCardUrl, resolveReference } = this.options
      const editor = this.editor

      /** True when an identical candidate is already in flight. */
      const alreadyPending = (
        state: EditorState,
        located: { from: number; to: number; text: string },
      ) =>
        (autodetectKey.getState(state)?.pending ?? []).some(
          (candidate) =>
            candidate.from === located.from &&
            candidate.to === located.to &&
            candidate.text === located.text,
        )

      /** The just-typed character, which is not in the document yet. */
      const typedCharacter = (match: RegExpMatchArray, range: { from: number; to: number }) => {
        const typedLength = match[0].length - (range.to - range.from)
        return { typedLength, typedDelimiter: match[0].slice(match[0].length - typedLength) }
      }

      return [
        new InputRule({
          find: REFERENCE_TRIGGER,
          handler: ({ state, range, match }) => {
            const { typedLength, typedDelimiter } = typedCharacter(match, range)
            const located = locateCandidate(
              state,
              range.to,
              typedLength,
              getProjectCodes,
              typedDelimiter,
            )
            // Returning without dispatching a step is what lets the typed
            // delimiter through untouched. This rule NEVER mutates the doc.
            if (!located) return null
            if (alreadyPending(state, located)) return null

            trackCandidate(
              editor,
              {
                id: nextCandidateId++,
                ...located,
                reference: { by: 'shorthand', shorthand: located.text.toUpperCase() },
                source: { kind: 'shorthand' },
              },
              resolveReference,
            )
            return null
          },
        }),
        new InputRule({
          find: TYPED_URL_TRIGGER,
          handler: ({ state, range, match }) => {
            const { typedLength, typedDelimiter } = typedCharacter(match, range)
            const located = locateUrlCandidate(
              state,
              range.to,
              typedLength,
              parseCardUrl,
              typedDelimiter,
            )
            if (!located) return null
            if (alreadyPending(state, located)) return null

            trackCandidate(
              editor,
              {
                id: nextCandidateId++,
                from: located.from,
                to: located.to,
                text: located.text,
                reference: located.reference,
                source: { kind: 'url', href: located.href },
              },
              resolveReference,
            )
            return null
          },
        }),
      ]
    },
  })
}

export { REFERENCE_TRIGGER, TYPED_URL_TRIGGER, locateCandidate, autodetectKey }
